// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useState } from 'react'
import {
  BasicMenu,
  Button,
  Flex,
  Typography,
  useShowHide,
} from '@teamnine/pcj-ui-v2'
import { KeyboardArrowDown } from '@/utils/material-icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'

import { formatDateTime } from '@/utils/date'
import { Customer as ICustomer, getCustomer } from '@/services/customers'
import {
  Tenant as ITenant,
  getTenant,
  refreshPractice,
} from '@/services/practices'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { getTagColor, getTagLabel } from '@/utils/helpers'
import { isStgOrPrd } from '@/utils/environment'
import { allPracticeServicesSuccessfullyConfigured } from '@/app/CustomerManagement/utils/practice-services'
import { useErrorHelper } from '@/app/shared/hooks/useErrorHelper'
import Page from '@/app/shared/components/Layout/Page'
import DetailItem from '@/app/shared/components/DetailItem'
import ExportPracticeModal from '@/app/CustomerManagement/components/ExportPracticeModal'
import ProvisioningLogModal from '@/app/CustomerManagement/components/ProvisioningLogModal'
import DeployDataModal from '@/app/CustomerManagement/components/DeployDataModal'
import PageHeader from '@/app/shared/components/PageHeader'
import LinkButton from '@/app/shared/components/LinkButton'
import Panel from '@/app/shared/components/Panel'

export default function Practice() {
  const { customerId, practiceId } = useParams()
  const { basicErrorHandler, displayGlobalMessage } = useErrorHelper()

  if (!customerId || !practiceId) {
    throw new Error('Missing customerId or practiceId in URL')
  }
  const [isRefreshing, setIsRefreshing] = useState(false)
  const exportPracticeModal = useShowHide()
  const provisioningLogModal = useShowHide()
  const deployDataModal = useShowHide()
  const navigate = useNavigate()
  const isAdmin = useIsAdmin()
  const domain = import.meta.env.VITE_DOMAIN

  const {
    data: tenantData,
    isLoading: isTenantLoading,
    refetch: refetchTenant,
  } = useQuery(['getTenant', practiceId], () => getTenant(practiceId))
  const { data: customerData, isLoading: isCustomerLoading } = useQuery(
    ['getCustomer', customerId],
    () => getCustomer(customerId)
  )
  const currentPractice = tenantData?.tenant as ITenant | undefined
  const currentCustomer = customerData?.customer as ICustomer | undefined
  const isLoading = isTenantLoading || isCustomerLoading

  const canDeployData =
    currentPractice &&
    !currentPractice.archived &&
    allPracticeServicesSuccessfullyConfigured(currentPractice)

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true)
      if (currentPractice?.practiceId) {
        await refreshPractice(currentPractice.practiceId)
        let refreshedTenant = (await refetchTenant()).data?.tenant
        if (!currentPractice.auth0OrgId && !refreshedTenant?.auth0OrgId) {
          const MAX_ATTEMPTS = 10
          const POLL_INTERVAL_MS = 3000
          for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
            await new Promise((resolve) =>
              setTimeout(resolve, POLL_INTERVAL_MS)
            )
            refreshedTenant = (await refetchTenant()).data?.tenant
            if (refreshedTenant?.auth0OrgId) {
              break
            }
          }
        }
        displayGlobalMessage({
          heading:
            refreshedTenant?.auth0OrgId || currentPractice.auth0OrgId
              ? 'Practice data refreshed successfully.'
              : 'Practice refresh completed. Auth0 organization sync is still in progress.',
          type: 'success',
        })
      } else {
        displayGlobalMessage({
          heading: 'No practice ID found for this practice.',
          type: 'danger',
        })
      }
    } catch (error) {
      console.error('Error refreshing practice:', error)
      basicErrorHandler(error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const options = [
    {
      label: 'Refresh',
      onSelect: handleRefresh,
      disabled: isRefreshing,
    },
    {
      label: 'Deploy Data',
      onSelect: deployDataModal.show,
      disabled:
        !canDeployData ||
        (isStgOrPrd() && !isAdmin && !!currentCustomer?.accountGcn),
    },
    {
      label: 'Export',
      onSelect: exportPracticeModal.show,
    },
    {
      label: 'Provisioning Log',
      onSelect: provisioningLogModal.show,
    },
  ]

  return (
    <Page>
      <Page.Content>
        <Panel>
          <Flex direction="column" gap={16}>
            <PageHeader
              title={currentPractice?.name ?? ''}
              tag={{
                label: getTagLabel(currentCustomer),
                color: getTagColor(currentCustomer),
              }}
              action={
                <BasicMenu
                  aria-label="Open practice actions"
                  modal={false}
                  menuOptions={options}
                >
                  <Button
                    data-testid="customer-management-practice-actions-menu-btn"
                    iconRight={<KeyboardArrowDown />}
                  >
                    Actions
                  </Button>
                </BasicMenu>
              }
              breadcrumbItems={[
                {
                  label: 'Customer Management',
                  to: '/customer-management',
                },
                {
                  label: currentCustomer?.name ?? '',
                  to: `/customer-management/${currentCustomer?.id}`,
                },
                {
                  label: currentPractice?.name ?? '',
                },
              ]}
              isLoading={isLoading}
            />
            <Typography variant="header-m">Overview</Typography>
            <Flex justify="space-between">
              <Flex direction="column" gap={8}>
                <DetailItem label="Site ID" value={currentPractice?.siteId} />
                <DetailItem label="Tenant ID" value={currentPractice?.id} />
                <DetailItem
                  label="Links"
                  value={
                    <Flex direction="column" gap={8}>
                      <LinkButton
                        label="Atrium"
                        onClick={() =>
                          window.open(
                            `https://${currentPractice?.subdomain}.apps.${domain}`,
                            '_blank'
                          )
                        }
                      />
                      <LinkButton
                        label="Source Template"
                        onClick={() =>
                          navigate(
                            `/templates/${currentPractice?.createdFromTemplateId}`
                          )
                        }
                      />
                    </Flex>
                  }
                />
              </Flex>
              <Flex direction="column" gap={8}>
                <DetailItem
                  label="Practice ID"
                  value={currentPractice?.practiceId}
                />
                <DetailItem
                  label="Enterprise Practice ID"
                  value={currentPractice?.enterprisePracticeId}
                />
                <DetailItem
                  label="Auth0 Organization ID"
                  value={currentPractice?.auth0OrgId}
                />
              </Flex>
              <Flex direction="column" gap={8}>
                <DetailItem
                  label="Enterprise ID"
                  value={currentPractice?.enterpriseId}
                />
                <DetailItem
                  label="Database Cluster"
                  value={currentPractice?.dbCluster}
                />
              </Flex>
              <Flex direction="column">
                <DetailItem label="GCN" value={currentCustomer?.accountGcn} />
                <DetailItem
                  label="Creation Time"
                  value={formatDateTime(currentPractice?.createdAt)}
                />
              </Flex>
              <Flex />
            </Flex>
          </Flex>
        </Panel>
        {currentPractice && (
          <>
            <ProvisioningLogModal
              modal={provisioningLogModal}
              practice={currentPractice}
            />
            <ExportPracticeModal
              modal={exportPracticeModal}
              practice={currentPractice}
            />
            <DeployDataModal
              modal={deployDataModal}
              practiceId={currentPractice?.id}
            />
          </>
        )}
      </Page.Content>
    </Page>
  )
}
